import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  GridItem,
  Select,
  Textarea,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import ColorPicker from 'src/components/atoms/color-picker';
import { MarketplaceInfo, ThemeOptions, ThemeVariables } from 'src/api/v1-api';
import { parseUnderscoreName } from 'src/utils/common';
import { getFontFamily, preloadGoogleFonts } from 'src/utils/dynamicFonts';
import Link from 'next/link';
import useRequest from 'src/utils/hooks/useRequest';

export interface BrandingFormFields {
  logoIsCircle: boolean;
  showTotalSocialmediaAudience: boolean;
  marketplaceColors: MarketplaceColors;
  marketplaceThemeOptions: ThemeVariables;
  logoPhotoFile: File | null;
  name?: string;
  description?: string;
}

export interface MarketplaceColors {
  main: string;
  nav: string;
  primary: string;
  secondary: string;
}

export interface BrandingFormProps {
  marketplace: MarketplaceInfo | null;
  logoIsCircle: boolean;
  onLogoIsCircleChange: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  onSubmit: (data: BrandingFormFields) => void;
  showNameAndDescription?: boolean;
}

const fontOptions = ['font_1', 'font_2'];
const DEFAULT_MAIN_COLOR = '#ffffff';
const DEFAULT_NAV_COLOR = '#ffffff';
const DEFAULT_PRIMARY_COLOR = '#3182CE';
const DEFAULT_SECONDARY_COLOR = '#319795';

export const BrandingForm = ({
  marketplace,
  logoIsCircle,
  onLogoIsCircleChange,
  isLoading,
  onSubmit,
  showNameAndDescription = false,
}: BrandingFormProps) => {
  const [logoPhoto, setLogoPhoto] = useState('');
  const [showTotalSocialmediaAudience, setShowTotalSocialmediaAudience] = useState(true);
  const [logoPhotoFile, setLogoPhotoFile] = useState<File | null>(null);
  const [themeOptions, setThemeOptions] = useState<ThemeOptions>({} as ThemeOptions);
  const [marketplaceThemeOptions, setMarketplaceThemeOptions] = useState<ThemeVariables>(
    {} as ThemeVariables,
  );
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [marketplaceColors, setMarketplaceColors] = useState<MarketplaceColors>({
    main:
      marketplace?.theme?.background_color && marketplace?.theme?.background_color !== ''
        ? marketplace?.theme?.background_color
        : DEFAULT_MAIN_COLOR,
    nav:
      marketplace?.theme?.nav_background_color && marketplace?.theme?.nav_background_color !== ''
        ? marketplace?.theme?.nav_background_color
        : DEFAULT_NAV_COLOR,
    primary:
      marketplace?.theme?.primary_color && marketplace?.theme?.primary_color !== ''
        ? marketplace?.theme?.primary_color
        : DEFAULT_PRIMARY_COLOR,
    secondary:
      marketplace?.theme?.secondary_color && marketplace?.theme?.secondary_color !== ''
        ? marketplace?.theme?.secondary_color
        : DEFAULT_SECONDARY_COLOR,
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { api } = useRequest();

  const getMarketplaceOptions = async () => {
    if (!marketplace?.id) return;
    const response = await api.marketplace.theme.options.get(marketplace?.id);
    const filteredOptions = Object.fromEntries(
      Object.entries(response.options).filter(
        ([key, _option]) =>
          ![
            'primary_color',
            'secondary_color',
            'nav_background_color',
            'background_color',
            'component_background_color',
          ].includes(key),
      ),
    );
    setThemeOptions(filteredOptions as ThemeOptions);

    // Preload all available fonts for font dropdowns
    if (typeof window !== 'undefined') {
      const allFonts: string[] = [];
      Object.entries(filteredOptions).forEach(([key, option]) => {
        if (fontOptions.includes(key) && (option as any).enum) {
          allFonts.push(...(option as any).enum);
        }
      });
      preloadGoogleFonts(...allFonts);

      // Wait for fonts to be loaded
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          setFontsLoaded(true);
        });
      } else {
        // Fallback for browsers that don't support document.fonts.ready
        setTimeout(() => {
          setFontsLoaded(true);
        }, 1000);
      }
    }
  };

  useEffect(() => {
    if (marketplace) {
      getMarketplaceOptions();
      setLogoPhoto(marketplace.logo_image);
      setShowTotalSocialmediaAudience(
        marketplace.functional?.show_total_social_media_audience ?? false,
      );
      setMarketplaceThemeOptions(marketplace.theme);
      setName(marketplace.name || '');
      setDescription(marketplace.description || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace]);

  if (!marketplace) {
    return <div>Loading...</div>;
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    setLogoPhotoFile(file);
    if (file) {
      setLogoPhoto(URL.createObjectURL(file));
    }
  };

  const handleColorChange = (colorName: keyof MarketplaceColors, color: string) => {
    setMarketplaceColors({ ...marketplaceColors, [colorName]: color });
  };

  const handleMarketplaceThemeOptionsChange = (key: string, value: string) => {
    const newOptions = { ...marketplaceThemeOptions };
    (newOptions as any)[key] = value;
    setMarketplaceThemeOptions(newOptions);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (showNameAndDescription) {
      onSubmit({
        name,
        description,
        logoPhotoFile,
        logoIsCircle,
        showTotalSocialmediaAudience,
        marketplaceThemeOptions,
        marketplaceColors,
      });
    } else {
      onSubmit({
        logoPhotoFile,
        logoIsCircle,
        showTotalSocialmediaAudience,
        marketplaceThemeOptions,
        marketplaceColors,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Stack justify="flex-start" align="flex-start" spacing="32px" w="100%">
        <Stack spacing="24px" w="100%">
          {showNameAndDescription && (
            <>
              <FormControl>
                <FormLabel fontWeight="medium" mb="8px">
                  Name
                </FormLabel>
                <Input
                  placeholder="Marketplace Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="medium" mb="8px">
                  Description
                </FormLabel>
                <Textarea
                  placeholder="Describe your marketplace"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={4}
                  data-test="description-input"
                />
              </FormControl>
            </>
          )}

          <FormControl display="flex" alignItems="center">
            <Box flex="1">
              <FormLabel fontWeight="medium" mb="4px">
                Logo Photo
              </FormLabel>
              <Text fontSize="sm" color="gray.600">
                Upload your marketplace logo
              </Text>
            </Box>
            <Flex align="center" gap={4}>
              {logoPhoto && (
                <Avatar
                  borderRadius={logoIsCircle ? 'full' : ''}
                  src={logoPhoto}
                  boxSize={12}
                  objectFit="cover"
                />
              )}
              <Box>
                <Input
                  type="file"
                  id="file-upload"
                  onChange={handleImageUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload">
                  <Button as="span" colorScheme="primary" variant="outline" size="sm">
                    Choose File
                  </Button>
                </label>
              </Box>
            </Flex>
          </FormControl>

          <Divider />

          <FormControl display="flex" alignItems="center">
            <Box flex="1">
              <FormLabel fontWeight="medium" mb="4px">
                Rounded Logo
              </FormLabel>
              <Text fontSize="sm" color="gray.600">
                Apply rounded corners to your logo
              </Text>
            </Box>
            <Switch
              size="lg"
              isChecked={logoIsCircle}
              onChange={(event) => onLogoIsCircleChange(event.target.checked)}
            />
          </FormControl>

          <Divider />

          <FormControl display="flex" alignItems="center">
            <Box flex="1">
              <FormLabel fontWeight="medium" mb="4px">
                Show Social Media Total Followers in Header
              </FormLabel>
              <Text fontSize="sm" color="gray.600">
                Display total follower count across all connected social media accounts
              </Text>
            </Box>
            <Switch
              size="lg"
              isChecked={showTotalSocialmediaAudience}
              onChange={(event) => setShowTotalSocialmediaAudience(event.target.checked)}
            />
          </FormControl>

          <Divider />
        </Stack>

        <Stack spacing="24px" w="100%">
          <Box>
            <Text fontSize="lg" fontWeight="semibold" mb="8px">
              Marketplace Colors
            </Text>
            <Text fontSize="sm" color="gray.600" mb="20px">
              Customize the color scheme of your marketplace
            </Text>
          </Box>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="flex-start"
            align="flex-start"
            spacing="20px"
            w="100%"
          >
            <FormControl flex="1">
              <FormLabel fontWeight="medium" mb="8px">
                Main Background Color
              </FormLabel>
              <ColorPicker
                value={marketplaceColors.main}
                onChange={(color) => handleColorChange('main', color)}
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel fontWeight="medium" mb="8px">
                Nav Bar Background Color
              </FormLabel>
              <ColorPicker
                value={marketplaceColors.nav}
                onChange={(color) => handleColorChange('nav', color)}
              />
            </FormControl>
          </Stack>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="flex-start"
            align="flex-start"
            spacing="20px"
            w="100%"
          >
            <FormControl flex="1">
              <FormLabel fontWeight="medium" mb="8px">
                Primary Color
              </FormLabel>
              <Text fontSize="sm" color="gray.600" mb="8px">
                Used for links, buttons, and interactive elements
              </Text>
              <ColorPicker
                value={marketplaceColors.primary}
                onChange={(color) => handleColorChange('primary', color)}
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel fontWeight="medium" mb="8px">
                Secondary Color
              </FormLabel>
              <Text fontSize="sm" color="gray.600" mb="8px">
                Used for secondary elements and accents
              </Text>
              <ColorPicker
                value={marketplaceColors.secondary}
                onChange={(color) => handleColorChange('secondary', color)}
              />
            </FormControl>
          </Stack>
        </Stack>

        <Divider />

        <Stack spacing="24px" w="100%">
          <Box>
            <Text fontSize="lg" fontWeight="semibold" mb="8px">
              Theme Options
            </Text>
            <Text fontSize="sm" color="gray.600" mb="20px">
              Advanced theme customization settings
            </Text>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} rowGap={6} columnGap={6} width="full">
            {themeOptions &&
              Object.keys(themeOptions).map((key) => (
                <GridItem colSpan={1} key={key}>
                  <FormControl>
                    <FormLabel fontWeight="medium" mb="8px">
                      {key === 'font_1'
                        ? 'Heading Font'
                        : key === 'font_2'
                        ? 'Text Font'
                        : parseUnderscoreName(key)}
                    </FormLabel>
                    {fontOptions.includes(key) ? (
                      <Menu>
                        <MenuButton
                          as={Button}
                          rightIcon={<ChevronDownIcon />}
                          variant="outline"
                          textAlign="left"
                          justifyContent="space-between"
                          width="100%"
                          bg="white"
                          borderColor="gray.200"
                          _hover={{ borderColor: 'gray.300' }}
                          _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                          fontFamily={
                            fontsLoaded
                              ? getFontFamily((marketplaceThemeOptions as any)[key])
                              : 'inherit'
                          }
                          fontWeight="500"
                        >
                          {(marketplaceThemeOptions as any)[key] ||
                            `Select ${parseUnderscoreName(key)}`}
                        </MenuButton>
                        <MenuList
                          maxHeight="300px"
                          overflowY="scroll"
                          sx={{
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: '#c1c1c1',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                              background: '#a8a8a8',
                            },
                          }}
                        >
                          {(themeOptions as any)[key].enum?.map((option: string) => (
                            <MenuItem
                              key={option}
                              onClick={() => handleMarketplaceThemeOptionsChange(key, option)}
                              fontFamily={fontsLoaded ? getFontFamily(option) : 'inherit'}
                              fontWeight="500"
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    ) : (
                      <Select
                        name={key}
                        placeholder={`Select ${parseUnderscoreName(key)}`}
                        value={(marketplaceThemeOptions as any)[key]}
                        onChange={(e) => handleMarketplaceThemeOptionsChange(key, e.target.value)}
                      >
                        {(themeOptions as any)[key].enum?.map((option: string) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                </GridItem>
              ))}
          </SimpleGrid>
        </Stack>
      </Stack>
      <Flex direction={'column'} alignItems={'flex-end'} pt={10} gap={3}>
        <Box display="flex" justifyContent="flex-end">
          <Link href="/">
            <Button variant="outline" colorScheme="primary" mr={4}>
              Cancel
            </Button>
          </Link>
          <Button isLoading={isLoading} colorScheme="primary" type="submit" data-test="submit-btn">
            Save
          </Button>
        </Box>
        <Text fontSize="xs" color="gray.600" fontStyle="italic" textAlign="right">
          You may need to refresh the page to see changes after saving.
        </Text>
      </Flex>
    </form>
  );
};

export default BrandingForm;
